import ExcelJS from "exceljs";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { IFloorPayload } from "../Domain/Types/FloorPlan/FloorPayload.type";
import { IPlaceSchedule } from "../Domain/Types/FloorPlan/PlaceSchedule";
import { IZoneSchedule } from "../Domain/Types/FloorPlan/ZoneSchedule";
import { RemoteCompany } from "../../../hooks/Remote/Companies/useRemoteFetchCompanies";
import { CompanyUnits } from "../../../hooks/Remote/CompanyUnits/useRemoteFetchCompanyUnits";
import { Colleague } from "../../../features/Connections/types/Colleague.type";
import { generatePermRows } from "../Functions/generatePermRows";
import { exportExcelByDocument } from "../Functions/exportExcelByDocument";

export function useExportFloorPlanToExcel(
  floorPlan: IFloorPayload,
  floorPlanName: string,
  remoteUnits: {
    remoteCompanies: RemoteCompany[] | undefined;
    remoteCompanyUnits: CompanyUnits[] | undefined;
    remoteUsers: Colleague[] | undefined;
  }
) {
  const { t, i18n } = useTranslation();

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();

    /**
     * part of place information
     */
    const placeInfo = workbook.addWorksheet(t("Place Information"));
    // define columns for place information
    placeInfo.columns = [
      { header: t("Id"), key: "placeInventoryId", width: 10 },
      { header: t("Type"), key: "placeType", width: 15 },
      { header: t("Name"), key: "placeInventoryName", width: 20 },
      { header: t("Description"), key: "placeDescription", width: 30 },
      { header: t("Width"), key: "placeInventoryWidth", width: 7 },
      { header: t("Height"), key: "placeInventoryHeight", width: 7 },
      { header: t("Active"), key: "isActive", width: 7 },
      { header: t("Approval required"), key: "approvalRequired", width: 16 }
    ];
    // convert floor plan places to column data
    const placeInfoData = floorPlan.places.map(place => ({
      placeInventoryId: genPlaceInventoryId(place),
      placeType: t(genPlaceTypeId(place)),
      placeInventoryName: genPlaceInventoryName(place, t),
      placeDescription: place.description,
      placeInventoryWidth: place.inventory?.boundingBox.width,
      placeInventoryHeight: place.inventory?.boundingBox.height,
      isActive: genBoolean(!place.disabled, t),
      approvalRequired: genBoolean(place.approvalRequired, t)
    }));
    // Add data rows to the sheet
    placeInfoData.forEach(item => placeInfo.addRow(item));

    /**
     * part of zone information
     */
    const zoneInfo = workbook.addWorksheet(t("Zone Information"));
    // define columns for zone information
    zoneInfo.columns = [
      { header: t("Id"), key: "zoneInventoryId", width: 10 },
      { header: t("Type"), key: "zoneType", width: 15 },
      { header: t("Name"), key: "zoneInventoryName", width: 20 },
      { header: t("Description"), key: "zoneDescription", width: 30 },
      { header: t("Active"), key: "isActive", width: 7 },
      { header: t("Approval required"), key: "approvalRequired", width: 16 },
      { header: t("Min. number of user"), key: "zoneMinUsers", width: 7 },
      { header: t("Max. number of user"), key: "zoneMaxUsers", width: 7 }
    ];
    // convert floor plan zones to column data
    const zoneInfoData = floorPlan.zones.map(zone => ({
      zoneInventoryId: genZoneInventoryId(zone),
      zoneType: t(genZoneTypeId(zone)),
      zoneInventoryName: genZoneInventoryName(zone, t),
      zoneDescription: zone.description,
      isActive: genBoolean(!zone.disabled, t),
      approvalRequired: genBoolean(zone.approvalRequired, t),
      zoneMinUsers: zone.minUsers,
      zoneMaxUsers: zone.maxUsers
    }));
    // Add data rows to the sheet
    zoneInfoData.forEach(item => zoneInfo.addRow(item));

    /**
     * part of place equipment
     */
    const placeEquip = workbook.addWorksheet(t("Place Equipment"));
    // define columns for place equipment
    placeEquip.columns = [
      { header: t("Id"), key: "placeInventoryId", width: 10 },
      { header: t("Type"), key: "placeType", width: 15 },
      { header: t("Name"), key: "placeInventoryName", width: 20 },
      { header: t("Category"), key: "placeCategory", width: 20 },
      { header: t("Equipment Categories"), key: "placeEquipmentCategories", width: 30 },
      { header: t("Properties"), key: "placeProperties", width: 30 }
    ];
    // convert floor plan places to column data
    const placeEquipData = floorPlan.places.map(place => ({
      placeInventoryId: genPlaceInventoryId(place),
      placeType: t(genPlaceTypeId(place)),
      placeInventoryName: genPlaceInventoryName(place, t),
      placeCategory: place.category?.name,
      placeEquipmentCategories: place.equipmentCategories
        ?.map(
          equipCate => `${equipCate.quantity}x ${equipCate.name}` // '2x Monitor
        )
        .join(", "),
      placeProperties: place.bookingProperties?.map(prop => prop.name).join(", ")
    }));
    // Add data rows to the sheet
    placeEquipData.forEach(item => placeEquip.addRow(item));

    /**
     * part of zone equipment
     */
    const zoneEquip = workbook.addWorksheet(t("Zone Equipment"));
    // define columns for zone equipment
    zoneEquip.columns = [
      { header: t("Id"), key: "zoneInventoryId", width: 10 },
      { header: t("Type"), key: "zoneType", width: 15 },
      { header: t("Name"), key: "zoneInventoryName", width: 20 },
      { header: t("Category"), key: "zoneCategory", width: 20 },
      { header: t("Equipment Categories"), key: "zoneEquipmentCategories", width: 30 },
      { header: t("Properties"), key: "zoneProperties", width: 30 }
    ];
    // convert floor plan zones to column data
    const zoneEquipData = floorPlan.zones.map(zone => ({
      zoneInventoryId: genZoneInventoryId(zone),
      zoneType: t(genZoneTypeId(zone)),
      zoneInventoryName: genZoneInventoryName(zone, t),
      zoneCategory: zone.category?.name,
      zoneEquipmentCategories: zone.equipmentCategories
        ?.map(
          equipCate => `${equipCate.quantity}x ${equipCate.name}` // '2x Monitor
        )
        .join(", "),
      zoneProperties: zone.bookingProperties?.map(prop => prop.name).join(", ")
    }));
    // Add data rows to the sheet
    zoneEquipData.forEach(item => zoneEquip.addRow(item));

    /**
     * part of place assignment
     */
    const placeAssign = workbook.addWorksheet(t("Place Assignment"));
    // define columns for place assignment
    placeAssign.columns = [
      { header: t("Id"), key: "inventoryId", width: 10 },
      { header: t("Type"), key: "type", width: 15 },
      { header: t("Name"), key: "inventoryName", width: 20 },
      { header: t("Permission Type"), key: "permissionType", width: 15 },
      { header: t("Permitted is"), key: "permittedIs", width: 20 },
      { header: t("Spillover"), key: "spillover", width: 10 },
      { header: t("Permitted Weekdays"), key: "permittedWeekdays", width: 40 },
      { header: t("Lead Time (h)"), key: "leadTime", width: 12 }
    ];
    //
    const placesWithPerms = floorPlan.places.filter(place =>
      place.permissions.some(
        perm =>
          perm.affectedCompanies.length > 0 ||
          perm.affectedDepartments.length > 0 ||
          perm.affectedUsers.length > 0
      )
    );
    // convert floor plan places to column data
    const placeAssignData = placesWithPerms.flatMap(item =>
      generatePermRows(item.permissions, remoteUnits, t).map(row => ({
        ...row,
        inventoryId: genPlaceInventoryId(item),
        type: t(genPlaceTypeId(item)),
        inventoryName: genPlaceInventoryName(item, t)
      }))
    );
    // Add data rows to the sheet
    placeAssignData.forEach(item => placeAssign.addRow(item));

    /**
     * part of zone assignment
     */
    const zoneAssign = workbook.addWorksheet(t("Zone Assignment"));
    // define columns for place assignment
    zoneAssign.columns = [
      { header: t("Id"), key: "inventoryId", width: 10 },
      { header: t("Type"), key: "type", width: 15 },
      { header: t("Name"), key: "inventoryName", width: 20 },
      { header: t("Permission Type"), key: "permissionType", width: 15 },
      { header: t("Permitted is"), key: "permittedIs", width: 20 },
      { header: t("Spillover"), key: "spillover", width: 10 },
      { header: t("Permitted Weekdays"), key: "permittedWeekdays", width: 40 },
      { header: t("Lead Time (h)"), key: "leadTime", width: 12 }
    ];
    //
    const zonesWithPerms = floorPlan.zones.filter(zone =>
      zone.permissions.some(
        perm =>
          perm.affectedCompanies.length > 0 ||
          perm.affectedDepartments.length > 0 ||
          perm.affectedUsers.length > 0
      )
    );
    // convert floor plan zones to column data
    const zoneAssignData = zonesWithPerms.flatMap(item =>
      generatePermRows(item.permissions, remoteUnits, t).map(row => ({
        ...row,
        inventoryId: genZoneInventoryId(item),
        type: t(genZoneTypeId(item)),
        inventoryName: genZoneInventoryName(item, t)
      }))
    );
    // Add data rows to the sheet
    zoneAssignData.forEach(item => zoneAssign.addRow(item));

    // Create the Excel file buffer
    const buffer = await workbook.xlsx.writeBuffer();

    // language setting ?
    workbook.model.language = i18n.language;

    // Create a Blob from the buffer and download it
    const file = new Blob([buffer], { type: "application/octet-stream" });

    const url = URL.createObjectURL(file);
    // create element to save file
    exportExcelByDocument(url, floorPlanName);

    URL.revokeObjectURL(url); // Cleanup the URL object after use
  };

  return { exportToExcel };
}

export const genPlaceInventoryId = (place: IPlaceSchedule) =>
  place.inventory?.id || place.inventoryId || 0;

export const genPlaceTypeId = (place: IPlaceSchedule) =>
  findPlaceBookingType(place.inventory?.placeTypeId || place.placeTypeId || 1);

export const genPlaceInventoryName = (place: IPlaceSchedule, t: TFunction) =>
  place.inventory?.name || t("No Name");

export const genZoneInventoryId = (zone: IZoneSchedule) =>
  zone.inventory?.id || zone.inventoryId || 0;

export const genZoneTypeId = (zone: IZoneSchedule) =>
  findZoneBookingType(zone.inventory?.zoneTypeId || zone.zoneTypeId || 1);

export const genZoneInventoryName = (zone: IZoneSchedule, t: TFunction) =>
  zone.inventory?.name || t("No Name");

export const genBoolean = (isTrue: boolean, t: TFunction) => (isTrue ? t("Yes") : t("No"));

/** return place booking type enum from type id */
export function findPlaceBookingType(typeId: number) {
  switch (typeId) {
    case 1:
      return "Workplace";
    case 2:
      return "Parking";
    case 4:
      return "Electric Charging";
    case 0:
    default:
      return "Workplace";
  }
}

/** return zone booking type enum from type id */
export function findZoneBookingType(typeId: number) {
  switch (typeId) {
    case 1:
      return "Room";
    // case 2: return BookingType.OPENSPACE;
    case 3:
      return "_bookTypeMeetingRoom";
    // case 4: return BookingType.RESTRICTED;
    // case 0:
    default:
      return "Room";
  }
}
